import React from "react";
import { useStoreSelector } from "~/lib/store/hooks";
import { tenantOnboardingStore } from "../../store/tenantOnboardingStore";
import { TenantInfoForm } from "./TenantInfoForm";
import { validateStep1 } from "../../validation/step1Validation";
import { tenantOnboardingActions } from "../../store/tenantOnboardingStore";

/**
 * Step 1: General Information Container
 */
export const Step1TenantInfo: React.FC = () => {
  const step1Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step1Data,
  );
  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );
  const touchedFields = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.touchedFields,
  );

  const handleChange = (updates: Partial<typeof step1Data>) => {
    tenantOnboardingActions.updateStep1Data(updates);
    // Validate domains immediately when they change to catch duplicates
    if (updates.domains) {
      tenantOnboardingActions.markFieldTouched("domains");
      // Validate with updated data to catch duplicates immediately
      const updatedData = { ...step1Data, ...updates };
      const errors = validateStep1(updatedData);
      // Update validation errors, preserving other field errors
      const currentErrors = { ...validationErrors };

      // Preserve existing domain errors that are availability-related (not format/duplicate)
      const existingDomainErrors = currentErrors.domains || [];
      const availabilityErrors = existingDomainErrors.filter(
        (error) =>
          error.includes("already in use") ||
          error.includes("Unable to verify"),
      );

      if (errors.domains && errors.domains.length > 0) {
        // Merge validation errors with availability errors
        currentErrors.domains = [...errors.domains, ...availabilityErrors];
      } else if (availabilityErrors.length > 0) {
        // Keep availability errors even if validation passes
        currentErrors.domains = availabilityErrors;
      } else {
        // Only clear domain errors if there are no validation or availability errors
        delete currentErrors.domains;
      }
      tenantOnboardingActions.setValidationErrors(currentErrors);
    }
  };

  const handleBlur = (fieldName: string) => {
    // Mark field as touched
    tenantOnboardingActions.markFieldTouched(fieldName);

    // Validate only touched fields
    const errors = validateStep1(step1Data);
    const filteredErrors: Record<string, string[]> = {};

    // Only show errors for touched fields
    Object.keys(errors).forEach((key) => {
      if (touchedFields.has(key) || fieldName === key) {
        filteredErrors[key] = errors[key];
      }
    });

    tenantOnboardingActions.setValidationErrors(filteredErrors);
  };

  return (
    <TenantInfoForm
      data={step1Data}
      onChange={handleChange}
      onBlur={handleBlur}
      errors={validationErrors}
      touchedFields={touchedFields}
      setFieldError={(fieldName, messages) =>
        tenantOnboardingActions.setValidationErrors({
          ...validationErrors,
          [fieldName]: messages,
        })
      }
      clearFieldError={(fieldName) =>
        tenantOnboardingActions.clearFieldError(fieldName)
      }
      markFieldTouched={(fieldName) =>
        tenantOnboardingActions.markFieldTouched(fieldName)
      }
    />
  );
};
